<template>
	<Modal css-class="foo" fullscreen show-dismissible-close-button dismissible @close="close">
		<template #title>
			<div class="font-h3-bold pl-03 text-normal">{{ t(`upsell.title.${type}`) }}</div>
		</template>

		<div class="flex w-[64rem] flex-col items-start justify-center whitespace-pre-line">
			<div class="overflow-auto px-03">
				<div class="font-paragraph-m-medium">{{ t(`upsell.subtitle.${type}`) }}</div>

				<div class="font-paragraph-m mt-05 whitespace-pre-wrap">
					<T :key="`${isLoadingMissedRevenue}${upliftsSum}${missedRevenue}`" :path="`upsell.desc.${type}`">
						<template #missedRevenue>
							<div class="font-paragraph-m-medium inline-flex items-center">
								{{ t('upsell.descPlatformsHighlight') }}

								<Tooltip :hide-content="!upliftsSum || isLoadingMissedRevenue" :content="t('upsell.missedRevenueTooltip')">
									<Loader v-if="isLoadingMissedRevenue" size="normal" class="mx-02" />

									<span v-else class="text-message-warning">{{ missedRevenue }}&nbsp;</span>

									<span class="text-message-warning"> {{ t('upsell.descPlatformsMissedRevenue') }} </span>
								</Tooltip>
							</div>
						</template>
					</T>
				</div>

				<div class="mt-07 rounded bg-level-00 px-07 pb-05 pt-06">
					<div class="font-paragraph-m-medium text-center">
						<T path="upsell.indiebiHandles">
							<template #value>
								<span class="text-message-success">{{ t('upsell.indiebiHandlesValue') }}</span>
							</template>
						</T>
					</div>

					<div class="logos flex items-center">
						<img class="w-[4rem]" src="@/assets/aggrocrab-logo.png" />
						<img class="w-[4rem]" src="@/assets/boneloaf-logo.png" />
						<img class="w-[4rem]" src="@/assets/hinterland-logo.png" />
						<img class="w-[4rem]" src="@/assets/innersloth-logo.png" />
						<img class="w-[4rem]" src="@/assets/monomi-park-logo.png" />
						<img class="mx-[0.5rem] w-[3rem]" src="@/assets/mega-crit-logo.png" />
						<img class="mx-[0.5rem] w-[3rem]" src="@/assets/panic-logo.png" />
						<img class="w-[4rem]" src="@/assets/relic-logo.png" />
						<img class="mx-[1rem] w-[2rem]" src="@/assets/schell-games-logo.png" />
					</div>

					<div class="font-body-m text-center">{{ t('upsell.joinThem') }}</div>
				</div>

				<div class="w-full text-center">
					<div class="font-paragraph-m-medium mt-07">{{ t(`upsell.footer.${type}`) }}</div>
				</div>
			</div>

			<div class="w-full text-center">
				<Button class="mt-06" size="s" variant="primary" icon="calendar-multiselect" @click="contactUs">{{ t(`upsell.cta`) }}</Button>
			</div>
		</div>
	</Modal>
</template>

<script setup lang="ts">
import { Button, Loader, Modal, Tooltip, useTranslation } from '@bluebrick/saas-ui'
import numbro from 'numbro'
import { storeToRefs } from 'pinia'
import { computed, onMounted, onUnmounted, ref } from 'vue'
import { openLink } from '@/common/utils'
import { useConfigStore } from '@/features/config/useConfigStore'
import { useTelemetry } from '@/features/telemetry/useTelemetry'
import { useUpsellStore } from '@/features/upsell/useUpsellStore'
import T from '@/translations/T.vue'

const emit = defineEmits(['close'])

const { type } = defineProps<{
	type: 'platforms' | 'automation' | 'revenue'
}>()

const { trackEvent } = useTelemetry()
const { t } = useTranslation()
const { upliftsSum, isLoading } = storeToRefs(useUpsellStore())
const { config } = storeToRefs(useConfigStore())

// Used to create an impression that the uplift sum value is a result of some heavy calculation.
// Initially we show the loader in place of the number and hide it after both things happen:
// - uplift sum is fetched from API
// - a predefined "fake loading" timer expires (currently 3 seconds)
const isFakingUpliftSumCalculation = ref(true)

const missedRevenue = computed(() =>
	upliftsSum.value === undefined
		? t('upsell.descPlatformsMissedRevenuePlaceholder')
		: numbro(upliftsSum.value).formatCurrency({ thousandSeparated: true, currencySymbol: '$' })
)

const isLoadingMissedRevenue = computed(() => isLoading.value || isFakingUpliftSumCalculation.value)

let upliftSumLoadingTimeoutId: number | undefined

function contactUs() {
	trackEvent('user', 'Wants to contact us - upsell.', {
		upsellType: type
	})

	let url

	switch (type) {
		case 'automation':
			url = config.value.automationUpsellCalendarBookingUrl

			break
		case 'platforms':
			url = config.value.platformsUpsellCalendarBookingUrl

			break

		case 'revenue':
			url = config.value.unlockRevenueUpsellCalendarBookingUrl

			break

		default:
			url = config.value.generalCalendarBookingUrl
	}

	openLink(url)

	emit('close')
}

function close() {
	trackEvent('user', 'Closed upsell modal.', {
		upsellType: type
	})

	emit('close')
}

onMounted(() => {
	upliftSumLoadingTimeoutId = window.setTimeout(() => {
		isFakingUpliftSumCalculation.value = false
	}, 3000)
})

onUnmounted(() => {
	clearTimeout(upliftSumLoadingTimeoutId)
})
</script>

<style lang="scss" scoped>
$items_count: 9;
$delay: 3s;
.logos {
	position: relative;
	width: 53.125rem;
	height: 9.375rem;
	margin: 0 auto;
	overflow: hidden;
}

.logos img {
	position: absolute;
	left: 100%;
	animation: slider calc($items_count * $delay) infinite linear;
}

@for $i from 1 through $items_count {
	.logos img:nth-child(#{$i}) {
		animation-delay: -#{$delay * ($items_count - $i)};
	}
}

@keyframes slider {
	0% {
		left: 100%;
		visibility: visible;
	}
	99% {
		// gap between items
		left: -18rem;
		visibility: hidden;
	}
	100% {
		left: 100%;
		visibility: hidden;
	}
}
</style>
